// ─── Chin Bot Monitor v3 ──────────────────────────────────────
// Single workspace: select a pre-built symbol list from dropdown,
// then view a table with symbol names on the left and 2 function columns:
// Status (dropdown: null / Level 1 / Level 2) | Play
//
// Chin Bot works exclusively with 1H timeframe:
//   - Real-time tick-by-tick price data
//   - 100+ candle buffer with 1H indicators
//
// NOTE: This component is a thin render layer. All state + business
// logic lives in useChinMonitorLogic; the large JSX sections (top bar,
// force-check panel, symbol table) are extracted into focused
// presentational components under ./chin-monitor/. Behavior unchanged.

import type { Strategy } from '@/types/strategy';
import RiskDashboard from '@/pages/strategies/components/RiskDashboard';
import ActivityLog from '@/pages/strategies/components/ActivityLog';
import { useChinMonitorLogic } from './useChinMonitorLogic';
import { CHIN_TIMEFRAME, type CtraderAccount } from './chin-monitor/types';
import ChinMonitorTopBar from './chin-monitor/ChinMonitorTopBar';
import ChinForceCheckPanel from './chin-monitor/ChinForceCheckPanel';
import ChinSymbolTable from './chin-monitor/ChinSymbolTable';
import SymbolNoteModal from './chin-monitor/SymbolNoteModal';

// ─── Component ──────────────────────────────────────────────────

interface ChinMonitorProps {
  strategy: Strategy;
  ctraderAccounts?: CtraderAccount[];
  isActive: boolean;
  initialAccountId?: string;
}

export default function ChinMonitor({
  strategy,
  ctraderAccounts = [],
  isActive,
  initialAccountId,
}: ChinMonitorProps) {
  const m = useChinMonitorLogic({ strategy, ctraderAccounts, isActive, initialAccountId });

  const symbolList = m.engineState?.symbols ? Object.values(m.engineState.symbols) : [];

  return (
    <div className="space-y-4">
      {/* ─── Top Bar ─── */}
      <ChinMonitorTopBar
        symbolLists={m.symbolLists}
        loadingLists={m.loadingLists}
        selectedListId={m.selectedListId}
        selectedList={m.selectedList}
        dropdownOpen={m.dropdownOpen}
        onToggleDropdown={() => m.setDropdownOpen(!m.dropdownOpen)}
        dropdownRef={m.dropdownRef}
        onSelectList={m.handleSelectList}
        currentAccount={m.currentAccount}
        selectedAccountId={m.selectedAccountId}
        connected={m.connected}
        dailyResetFiredToday={m.dailyResetFiredToday}
        dailyResetFiredAt={m.dailyResetFiredAt}
        dailyCountdown={m.dailyCountdown}
        onResetAll={m.handleResetAll}
        onForceCheckAll={m.handleForceCheckAll}
        forceCheckRunning={m.forceCheckRunning}
        showActivityLog={m.showActivityLog}
        onToggleActivityLog={() => m.setShowActivityLog(!m.showActivityLog)}
        activityLogCount={m.activityLog.length}
        tma50AlertOn={m.tma50AlertOn}
        convergenceAlertOn={m.convergenceAlertOn}
        boxVsTma50AlertOn={m.boxVsTma50AlertOn}
        onToggleTma50Alert={m.handleToggleTma50Alert}
        onToggleConvergenceAlert={m.handleToggleConvergenceAlert}
        onToggleBoxVsTma50Alert={m.handleToggleBoxVsTma50Alert}
        rule4Enabled={m.rule4Enabled}
        onToggleRule4={m.handleToggleRule4}
        saveStatus={m.saveStatus}
        testTelegramRunning={m.testTelegramRunning}
        testTelegramResult={m.testTelegramResult}
        onTestTelegram={m.handleTestTelegram}
        isEngineRunning={m.isEngineRunning}
        isLoading={m.isLoading}
        loadedCount={symbolList.filter((s) => s.loaded).length}
        symbolCount={symbolList.length}
      />

      {/* ─── Force Check Results Panel ─── */}
      {m.forceCheckPanelOpen && (
        <ChinForceCheckPanel
          forceCheckRunning={m.forceCheckRunning}
          forceCheckResults={m.forceCheckResults}
          isLoading={m.isLoading}
          onClose={() => { m.setForceCheckPanelOpen(false); m.setForceCheckResults(null); }}
          onReRun={m.handleForceCheckAll}
        />
      )}

      {/* ─── Error Banner ─── */}
      {m.errorMsg && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-error-warning-line text-red-400 text-sm"></i>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-heading font-semibold text-red-400 mb-1">Data Load Error</h4>
            <p className="text-xs text-foreground-600 break-words">{m.errorMsg}</p>
          </div>
          <button
            type="button"
            onClick={() => m.startEngine()}
            className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap shrink-0"
          >
            <i className="ri-refresh-line mr-1"></i>
            Retry
          </button>
        </div>
      )}

      {/* ─── Loading state ─── */}
      {m.isLoading && m.selectedList && (
        <div className="bg-background-50 rounded-xl border border-dashed border-background-300/60 p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-background-200/70 flex items-center justify-center mx-auto mb-4">
            <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <h4 className="text-sm font-heading font-semibold text-foreground-900 mb-1">Loading 1H Market Data</h4>
          <p className="text-xs text-foreground-500 max-w-sm mx-auto">
            Fetching historical {CHIN_TIMEFRAME} candles for {m.selectedList.name} ({m.selectedList.symbols.length} symbols)...
            Target: 100+ candles per symbol.
          </p>
        </div>
      )}

      {/* ─── Empty: no list selected (restoring) ─── */}
      {!m.selectedList && !m.isLoading && m.workspaceLoading && (
        <div className="bg-background-50 rounded-xl border border-dashed border-background-300/60 p-16 text-center">
          <div className="w-16 h-16 rounded-2xl bg-background-200/70 flex items-center justify-center mx-auto mb-5">
            <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <h3 className="text-base font-heading font-semibold text-foreground-800 mb-2">Restoring Workspace</h3>
          <p className="text-sm text-foreground-500 max-w-md mx-auto">
            Loading your saved workspace from the cloud...
          </p>
        </div>
      )}

      {/* ─── Empty: no list selected ─── */}
      {!m.selectedList && !m.isLoading && !m.workspaceLoading && (
        <div className="bg-background-50 rounded-xl border border-dashed border-background-300/60 p-16 text-center">
          <div className="w-16 h-16 rounded-2xl bg-background-200/70 flex items-center justify-center mx-auto mb-5">
            <i className="ri-radar-line text-foreground-300 text-3xl"></i>
          </div>
          <h3 className="text-base font-heading font-semibold text-foreground-800 mb-2">Chin Bot Workspace</h3>
          <p className="text-sm text-foreground-500 max-w-md mx-auto">
            Choose a pre-built symbol list from the dropdown above to start monitoring.
            The system streams tick-by-tick real-time prices and maintains a 100+ candle {CHIN_TIMEFRAME} buffer for each symbol.
          </p>
        </div>
      )}

      {/* ─── Symbol Table ─── */}
      {!m.isLoading && m.selectedList && m.displaySymbols.length > 0 && (
        <ChinSymbolTable
          selectedList={m.selectedList}
          displaySymbols={m.displaySymbols}
          statusMap={m.statusMap}
          openStatusDropdown={m.openStatusDropdown}
          onToggleStatusDropdown={m.setOpenStatusDropdown}
          statusDropdownRef={m.statusDropdownRef}
          onStatusChange={m.handleStatusChange}
          playMap={m.playMap}
          symbolConfigs={m.symbolConfigs}
          evaluation={m.riskControl.evaluation}
          activeSymbolIds={m.activeSymbolIds}
          engineState={m.engineState}
          orderStatuses={m.orderStatuses}
          offEngineStatuses={m.offEngineStatuses}
          connected={m.connected}
          isEngineRunning={m.isEngineRunning}
          riskLoading={m.riskControl.isLoading}
          onRefreshPositions={m.riskControl.refreshPositions}
          onPriceChange={m.handlePriceChange}
          onMaxChange={m.handleMaxChange}
          onVolumeChange={m.handleVolumeChange}
          onDirectionChange={m.handleDirectionChange}
          onOrderDirectionChange={m.handleOrderDirectionChange}
          onResetSymbol={m.handleResetSymbol}
          onBoxXChange={m.handleBoxXChange}
          onBoxAlertToggle={m.handleBoxAlertToggle}
          onOpenNote={m.setOpenNoteSymbol}
        />
      )}

      {/* ─── Activity Log Panel ─── */}
      {m.showActivityLog && m.selectedList && (
        <ActivityLog
          entries={m.activityLog}
          onClear={() => m.setActivityLog([])}
          onClose={() => m.setShowActivityLog(false)}
        />
      )}

      {/* ─── Mini Risk Dashboard — floating bottom-right ─── */}
      {m.selectedList && (
        <RiskDashboard
          evaluation={m.riskControl.evaluation}
          positions={m.riskControl.positions}
          engineState={m.engineState}
          activeSymbolIds={m.activeSymbolIds}
          isLoading={m.riskControl.isLoading}
          onRefresh={m.riskControl.refreshPositions}
        />
      )}

      {/* ─── Symbol Note Modal ─── */}
      {m.openNoteSymbol != null && (() => {
        const noteSymbol = m.displaySymbols.find((s) => s.symbolId === m.openNoteSymbol);
        const noteText = m.symbolConfigs[m.openNoteSymbol]?.note || '';
        return (
          <SymbolNoteModal
            symbolName={noteSymbol?.symbolName || `#${m.openNoteSymbol}`}
            note={noteText}
            onSave={(val) => m.handleNoteChange(m.openNoteSymbol as number, val)}
            onClose={() => m.setOpenNoteSymbol(null)}
          />
        );
      })()}
    </div>
  );
}